// FOR REVIEW - review this whole file and all it's endpoints
// src/store.js
import {
  configureStore,
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { axiosInstance } from "services/axios-supabase";
import { supabase } from "services/supabase";
import _ from "lodash";

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  loading: false,
  error: null,
  noticesLoaded: false,
  notices: [],
};

export const selectNoticesLoaded = (state) =>
  state.notificationReducer.noticesLoaded;

export const fetchSupabaseNotificationLogs = createAsyncThunk(
  "data/fetchNotificationLogs",
  async ({ userId, ...params } = {}, { rejectWithValue }) => {
    try {
      if (!supabase) {
        throw new Error("Supabase client not initialized");
      }

      if (typeof supabase.from !== "function") {
        throw new Error("Supabase client methods not available");
      }

      if (!userId) {
        throw new Error("User not authenticated");
      }

      let query = supabase
        .from("logs")
        .select("*")
        .eq("uid", userId)
        .eq("status", "unread")
        .order("created_at", { ascending: false })
        .limit(5);

      const { data, error } = await query;

      if (error) {
        console.error("Query error:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error in fetchSupabaseNotificationLogs:", error);

      // Try a simpler approach if the chained query fails
      try {
        console.log("Trying simpler query approach...");
        const { data, error: simpleError } = await supabase
          .from("logs")
          .select("*")
          .eq("uid", userId)
          .eq("status", "unread")
          .limit(5);

        if (simpleError) {
          throw simpleError;
        }

        console.log("Simple query successful:", data);
        return data;
      } catch (simpleError) {
        console.error("Simple query also failed:", simpleError);
        return rejectWithValue(error.message || error.toString());
      }
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  "data/markNotificationAsRead",
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("Notification id is required");
      if (!userId) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("logs")
        .update({ status: "read" })
        .eq("id", id)
        .eq("uid", userId)
        .select("*")
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  "data/markAllNotificationsAsRead",
  async ({ userId }, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error("User not authenticated");

      // Mark all logs as read (do not rely on the UI-limited list).
      const { error } = await supabase
        .from("logs")
        .update({ status: "read" })
        .eq("uid", userId);

      if (error) throw error;
      return { userId };
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const notificationSubscribe = createAsyncThunk(
  "data/notificationSubscribe",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/notifications/subscribe`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const notificationUnSubscribe = createAsyncThunk(
  "data/notificationUnSubscribe",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/notifications/unsubscribe`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const notificationSendTest = createAsyncThunk(
  "data/notificationSendTest",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/notifications/send`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Real-time subscription actions removed - handled in component to avoid non-serializable state

// ------------------------------------------------------------------------
// Slices

export const NotificationSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    addLog: (state, action) => {
      state.notices.unshift(action.payload);
    },
    updateLog: (state, action) => {
      const index = state.notices.findIndex(
        (log) => log.id === action.payload.id
      );
      if (index !== -1) {
        state.notices[index] = action.payload;
      }
    },
    removeLog: (state, action) => {
      state.notices = state.notices.filter(
        (log) => log.id !== action.payload.id
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // ----------------------------------------------------------------
      .addCase(fetchSupabaseNotificationLogs.pending, (state) => {
        state.loading = true;
        // console.log('Fetching notices from Supabase...');
      })
      .addCase(fetchSupabaseNotificationLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.notices = action.payload || [];
        state.noticesLoaded = true;
        state.error = null;
        // fetched notices from Supabase:', action.payload);
      })
      .addCase(fetchSupabaseNotificationLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // console.log('❌ Failed to fetch notices from Supabase:', action.payload);
      })
      .addCase(markNotificationAsRead.pending, (state) => {
        // Avoid flipping global `loading` state for a single-row update,
        // since the UI uses `loading` for skeleton placeholders.
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated) return;

        const index = state.notices.findIndex((n) => n?.id === updated.id);
        if (index !== -1) {
          state.notices[index] = updated;
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(markAllNotificationsAsRead.pending, (state) => {
        // Avoid flipping global `loading` state for a bulk update.
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        // Local store is currently driven by unread-only queries.
        // Clearing keeps the UI consistent (chip + list should go empty).
        state.notices = [];
        state.noticesLoaded = true;
        state.error = null;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(notificationSubscribe.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(notificationUnSubscribe.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(notificationSendTest.fulfilled, (state, action) => {
        state.loading = false;
      });
  },
});

// Export actions and reducers
export const { setLoading, setError, addLog, updateLog, removeLog } =
  NotificationSlice.actions;

// ------------------------------------------------------------------------
// Configure store

export default NotificationSlice.reducer;
